import { registerPlugin } from "@capacitor/core";
import type { AnalyticsPort, CommonProps } from "./types";

/**
 * 앱 자체 플러그인 `Amplitude`(Android AmplitudePlugin.kt · iOS AmplitudePlugin.swift). 앱 안에서는 Browser SDK 를 쓰지 않는다.
 * 플러그인이 없거나 실패하면 전부 삼켜 no-op. sessionStart 는 플러그인이 리스너가 붙을 때까지 보관한다(retainUntilConsumed).
 */
type AmplitudePlugin = {
  /** iOS·ObjC 에서 init 은 초기화 메서드 계열이라 start */
  start(o: { apiKey: string; deviceId: string; commonProps: CommonProps }): Promise<void>;
  setCommonProps(o: { props: CommonProps }): Promise<void>;
  track(o: { event: string; props: Record<string, unknown> }): Promise<void>;
  setUserId(o: { userId: string | null }): Promise<void>;
  setUserProperties(o: { set?: Record<string, unknown>; setOnce?: Record<string, unknown> }): Promise<void>;
  getInstallReferrer(): Promise<{ referrer: string | null }>;
  flush(): Promise<void>;
  addListener(event: "sessionStart", cb: () => void): Promise<{ remove: () => Promise<void> }>;
};

const Amplitude = registerPlugin<AmplitudePlugin>("Amplitude");
const swallow = (p: Promise<unknown>) => p.catch(() => undefined);

export const nativeAnalytics: AnalyticsPort = {
  async init(o) {
    await swallow(Amplitude.start(o));
  },
  setCommonProps(props) {
    void swallow(Amplitude.setCommonProps({ props }));
  },
  track(event, props) {
    void swallow(Amplitude.track({ event, props }));
  },
  setUserId(userId) {
    void swallow(Amplitude.setUserId({ userId }));
  },
  setUserProperties(o) {
    void swallow(Amplitude.setUserProperties(o));
  },
  onSessionStart(cb) {
    const handle = Amplitude.addListener("sessionStart", cb).catch(() => null);
    return () => void handle.then((h) => h?.remove());
  },
  async getInstallReferrer() {
    // 일시 실패는 그대로 던져 다음 세션에 다시 조회하게 한다(포트 계약)
    return (await Amplitude.getInstallReferrer()).referrer ?? null;
  },
  async flush() {
    await swallow(Amplitude.flush());
  },
};
